<template>
  <div class="users-management">
    <v-container>
      <v-card>
        <v-card-title class="page-head">
          <div class="ph-left">
            <div class="ph-icon"><v-icon color="#37474f">mdi-account-multiple</v-icon></div>
            <div>
              <div class="ph-title">Users</div>
              <div class="ph-sub">Manage accounts &amp; district access</div>
            </div>
          </div>
          <v-spacer />
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search..."
            outlined
            dense
            hide-details
            class="ph-search"
          />
          <v-btn class="ph-btn" @click="openNewUser">
            <v-icon left small>mdi-plus</v-icon>
            New User
          </v-btn>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="users"
          :search="search"
          :loading="loadingUsers"
          loading-text="Loading users..."
          :items-per-page="-1"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, -1], 'items-per-page-text': 'Rows per page' }"
          class="elevation-0 users-table"
        >
          <template v-slot:item.index="{ index }">
            <span class="cell-muted">{{ index + 1 }}</span>
          </template>

          <template v-slot:item.pic="{ item }">
            <v-avatar size="38" class="avatar-ring">
              <v-img v-if="item.PicLoc" :src="picUrl(item.PicLoc)" />
              <v-icon v-else color="#90a4ae">mdi-account</v-icon>
            </v-avatar>
          </template>

          <template v-slot:item.FullName="{ item }">
            <span class="cell-name">{{ item.FullName }}</span>
          </template>

          <template v-slot:item.Username="{ item }">
            <span class="cell-muted">@{{ item.Username }}</span>
          </template>

          <template v-slot:item.Role="{ item }">
            <v-chip
              small
              :color="item.Role === 'admin' ? '#ede7f6' : '#e3f2fd'"
              :text-color="item.Role === 'admin' ? '#5e35b1' : '#1565c0'"
              class="role-chip"
            >
              <v-icon left x-small>{{ item.Role === 'admin' ? 'mdi-shield-crown' : 'mdi-account-edit' }}</v-icon>
              {{ item.Role }}
            </v-chip>
          </template>

          <template v-slot:item.district="{ item }">
            <v-chip v-if="item.district" small outlined color="teal">{{ item.district }}</v-chip>
            <span v-else class="cell-muted">All districts</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="Number(item.status) === 1 ? '#e8f5e9' : '#ffebee'"
              :text-color="Number(item.status) === 1 ? '#2e7d32' : '#c62828'"
            >
              <span class="status-dot" :class="Number(item.status) === 1 ? 'on' : 'off'"></span>
              {{ Number(item.status) === 1 ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="#2e7d32" v-bind="attrs" v-on="on" @click="editUser(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit user</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="#c62828" v-bind="attrs" v-on="on" @click="deleteUser(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete user</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="44" color="#b0bec5">mdi-account-off-outline</v-icon>
              <div class="empty-text">No users found</div>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Dialog: Create / Edit User -->
      <v-dialog v-model="dialog" max-width="640px" persistent>
        <v-card class="user-dialog">
          <div class="dlg-head">
            <div class="dlg-head-icon">
              <v-icon color="#fff">{{ dialogMode === 'create' ? 'mdi-account-plus' : 'mdi-account-edit' }}</v-icon>
            </div>
            <div>
              <div class="dlg-title">{{ dialogMode === 'create' ? 'Add User' : 'Edit User' }}</div>
              <div class="dlg-sub">{{ dialogMode === 'create' ? 'Create a new account' : 'Update details & district access' }}</div>
            </div>
            <v-spacer />
            <v-btn icon small class="dlg-close" @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="pt-5">
            <v-form ref="userForm">
              <div class="photo-row">
                <v-avatar size="64" class="avatar-ring lg">
                  <v-img v-if="picPreview" :src="picPreview" />
                  <v-img v-else-if="form.PicLoc" :src="picUrl(form.PicLoc)" />
                  <v-icon v-else size="30" color="#90a4ae">mdi-account</v-icon>
                </v-avatar>
                <v-file-input
                  v-model="picFile"
                  accept="image/*"
                  label="Profile photo"
                  prepend-icon="mdi-camera"
                  outlined
                  dense
                  hide-details
                  show-size
                  class="photo-input"
                  @change="onPicSelected"
                />
              </div>

              <div class="form-section">Account</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.FullName" label="Full Name" outlined dense prepend-inner-icon="mdi-account-outline" required />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.Username" label="Username" outlined dense prepend-inner-icon="mdi-at" required />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.Pass"
                    label="Password"
                    :type="showPass ? 'text' : 'password'"
                    outlined
                    dense
                    prepend-inner-icon="mdi-lock-outline"
                    :append-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showPass = !showPass"
                    required
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.Role" :items="roles" label="Role" outlined dense prepend-inner-icon="mdi-shield-account-outline" required />
                </v-col>
              </v-row>

              <div class="form-section">District Access</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.district"
                    :items="districts"
                    item-text="district_name"
                    item-value="district_name"
                    label="Primary District"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker-outline"
                    :disabled="form.Role === 'admin'"
                    hint="Admin sees all districts"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="managedDistricts"
                    :items="districts"
                    item-text="district_name"
                    item-value="id"
                    label="Managed Districts"
                    multiple
                    chips
                    small-chips
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker-multiple-outline"
                    :disabled="form.Role === 'admin'"
                    hint="All districts this user can access"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="form.status" :true-value="1" :false-value="0" label="Account active" inset color="green" class="mt-0" hide-details />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="dlg-actions">
            <v-spacer />
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="#0d2d63" dark :loading="saving" @click="saveUser">
              <v-icon left small>mdi-content-save-outline</v-icon>
              {{ dialogMode === 'create' ? 'Create User' : 'Save Changes' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL;
const UPLOAD_URL = process.env.VUE_APP_UPLOAD_URL;
const IMAGE_BASE = process.env.VUE_APP_IMAGE_BASE;

export default {
  data() {
    return {
      search: '',
      dialog: false,
      dialogMode: 'create',
      loadingUsers: false,
      saving: false,
      showPass: false,
      headers: [
        { text: 'No', value: 'index' },
        { text: 'Avatar', value: 'pic', sortable: false },
        { text: 'Full Name', value: 'FullName' },
        { text: 'Username', value: 'Username' },
        { text: 'Role', value: 'Role' },
        { text: 'District', value: 'district' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      roles: ['admin', 'editor'],
      users: [],
      districts: [],
      managedDistricts: [],
      originalManaged: [],
      picFile: null,
      picPreview: null,
      form: {
        UserID: null,
        FullName: '',
        Username: '',
        Pass: '',
        status: 1,
        Role: 'editor',
        PicLoc: '',
        district: '',
      },
    };
  },
  mounted() {
    this.fetchUsers();
    this.fetchDistricts();
  },
  methods: {
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const res = await axios.post(API_URL, {
          opcion: 16,
          op: 0,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        // Normalize status to numeric 1/0 so UI comparisons work
        this.users = rows.map(u => ({
          ...u,
          status: typeof u.status === 'string' ? parseInt(u.status, 10) || 0 : u.status,
        }));
      } catch (e) {
        console.error('Failed to load users', e);
      } finally {
        this.loadingUsers = false;
      }
    },
    async fetchDistricts() {
      try {
        const res = await axios.post(API_URL, { opcion: 40 });
        this.districts = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error('Failed to load districts', e);
      }
    },
    openNewUser() {
      this.dialogMode = 'create';
      this.form = {
        UserID: null,
        FullName: '',
        Username: '',
        Pass: '',
        status: 1,
        Role: 'editor',
        PicLoc: '',
        district: '',
      };
      this.managedDistricts = [];
      this.originalManaged = [];
      this.picFile = null;
      this.picPreview = null;
      this.dialog = true;
    },
    picUrl(name) {
      if (!name) return null;
      return /^https?:\/\//.test(name) ? name : `${IMAGE_BASE}${name}`;
    },
    onPicSelected(file) {
      if (!file) { this.picPreview = null; return; }
      this.picPreview = URL.createObjectURL(file);
    },
    async uploadPic() {
      if (!this.picFile) return this.form.PicLoc || '';
      const formData = new FormData();
      formData.append('file', this.picFile);
      const res = await axios.post(UPLOAD_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      const filename = typeof d === 'string' ? d
        : (d && (d.filename || d.path || d.file)) || '';
      return filename || this.form.PicLoc || '';
    },
    async editUser(user) {
      this.dialogMode = 'edit';
      this.form = {
        ...user,
        status: typeof user.status === 'string' ? parseInt(user.status, 10) || 0 : user.status,
      };
      this.managedDistricts = [];
      this.originalManaged = [];
      this.picFile = null;
      this.picPreview = null;
      try {
        const res = await axios.post(API_URL, {
          opcion: 41,
          op: 0,
          user_id: user.UserID,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        this.managedDistricts = rows
          .filter(r => Number(r.status) === 1)
          .map(r => r.district_id);
        this.originalManaged = [...this.managedDistricts];
      } catch (e) {
        console.error('Failed to load user districts', e);
      }
      this.dialog = true;
    },
    async syncDistricts(userId) {
      if (!userId) return;
      const me = JSON.parse(localStorage.getItem('user') || '{}');
      const toAdd = this.managedDistricts.filter(d => !this.originalManaged.includes(d));
      const toRemove = this.originalManaged.filter(d => !this.managedDistricts.includes(d));
      try {
        for (const districtId of toAdd) {
          await axios.post(API_URL, {
            opcion: 41,
            op: 1,
            user_id: userId,
            district_id: districtId,
            assigned_by: me.id || '',
          });
        }
        for (const districtId of toRemove) {
          await axios.post(API_URL, {
            opcion: 41,
            op: 12,
            user_id: userId,
            district_id: districtId,
          });
        }
      } catch (e) {
        console.error('Failed to sync districts', e);
      }
    },
    async saveUser() {
      this.saving = true;
      try {
        // Upload a newly chosen photo first so PicLoc is set before save
        if (this.picFile) {
          try {
            this.form.PicLoc = await this.uploadPic();
          } catch (e) {
            console.error('Photo upload failed', e);
          }
        }
        if (this.dialogMode === 'create') {
          await this.createUser();
        } else {
          await this.updateUser();
        }
        this.dialog = false;
        this.fetchUsers();
      } finally {
        this.saving = false;
      }
    },
    async createUser() {
      const payload = {
        opcion: 16,
        op: 1,
        UserID: this.form.UserID,
        FullName: this.form.FullName,
        Username: this.form.Username,
        Pass: this.form.Pass,
        Role: this.form.Role,
        PicLoc: this.form.PicLoc,
        status: this.form.status,
        district: this.form.district,
      };
      try {
        const res = await axios.post(API_URL, payload);
        const rows = Array.isArray(res.data) ? res.data : [];
        const newId = rows.length && rows[0].NewUserID ? rows[0].NewUserID : null;
        await this.syncDistricts(newId);
      } catch (e) {
        console.error('Failed to create user', e);
      }
    },
    async updateUser() {
      const payload = {
        opcion: 16,
        op: 3,
        UserID: this.form.UserID,
        FullName: this.form.FullName,
        Username: this.form.Username,
        Pass: this.form.Pass,
        Role: this.form.Role,
        PicLoc: this.form.PicLoc,
        status: this.form.status,
        district: this.form.district,
      };
      console.log(payload);
      try {
       const r= await axios.post(API_URL, payload);
       console.log(r.data);
       await this.syncDistricts(this.form.UserID);
      } catch (e) {
        console.error('Failed to update user', e);
      }
    },
    async deleteUser(user) {
      const payload = {
        opcion: 16,
        op: 12,
        UserID: user.UserID,
      };
      try {
        await axios.post(API_URL, payload);
        this.fetchUsers();
      } catch (e) {
        console.error('Failed to delete user', e);
      }
    },
  },
};
</script>

<style scoped>
.users-management {
  margin-top: 20px;
}

/* ===== Page head ===== */
.page-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-bottom: 1px solid #eef1f6;
}
.ph-left { display: flex; align-items: center; gap: 12px; }
.ph-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: #eceff4; display: flex; align-items: center; justify-content: center;
}
.ph-title { font-size: 1.05rem; font-weight: 700; color: #16233f; }
.ph-sub { font-size: .75rem; color: #8a94a6; }
.ph-search { max-width: 260px; }
.ph-btn {
  background: #0d2d63 !important; color: #fff !important;
  text-transform: none; font-weight: 600; border-radius: 8px;
}

/* ===== Table ===== */
.users-table ::v-deep thead th {
  background: #f7f9fc !important;
  color: #5b6b84 !important;
  font-size: .72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: .5px;
  border-bottom: 1px solid #e6eaf2 !important;
  white-space: nowrap;
}
.users-table ::v-deep tbody tr { transition: background .15s ease; }
.users-table ::v-deep tbody tr:hover { background: #f4f8ff !important; }
.users-table ::v-deep tbody td {
  border-bottom: 1px solid #f0f3f8 !important;
  font-size: .85rem;
  color: #33415c;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.avatar-ring {
  border: 2px solid #e3e9f2;
  background: #f4f6fa;
}
.avatar-ring.lg { border-width: 3px; }

.cell-name { font-weight: 600; color: #16233f; }
.cell-muted { color: #8a94a6; font-size: .82rem; }

.role-chip { font-weight: 600; text-transform: capitalize; }

.status-dot {
  width: 7px; height: 7px; border-radius: 50%;
  display: inline-block; margin-right: 6px;
}
.status-dot.on { background: #2e7d32; }
.status-dot.off { background: #c62828; }

.empty-state {
  padding: 40px 0;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.empty-text { color: #8a94a6; font-size: .85rem; }

/* ===== Dialog ===== */
.user-dialog { border-radius: 14px !important; overflow: hidden; }
.dlg-head {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #0d2d63 0%, #1a4a8a 100%);
}
.dlg-head-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
}
.dlg-title { color: #fff; font-size: 1rem; font-weight: 700; }
.dlg-sub { color: rgba(255,255,255,.7); font-size: .72rem; }
.dlg-close { color: rgba(255,255,255,.8) !important; }

.photo-row {
  display: flex; align-items: center; gap: 16px;
  padding: 4px 4px 16px;
}
.photo-input { flex: 1; }

.form-section {
  font-size: .7rem; font-weight: 700; letter-spacing: .8px;
  text-transform: uppercase; color: #8a94a6;
  margin: 8px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eef1f6;
}

.dlg-actions {
  padding: 12px 20px 16px;
  border-top: 1px solid #eef1f6;
}
</style>
